import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BreedersList = () => {
  const navigate = useNavigate();
  const [breeders, setBreeders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';

  useEffect(() => {
    const fetchBreeders = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/breeders`);
        if (!res.ok) throw new Error('Failed to fetch breeders');
        const data = await res.json();
        setBreeders(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBreeders();
  }, [API_URL]);

  const filtered = breeders.filter((b) => {
    const matchName = b.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      b.businessName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchLoc = !locationFilter ||
                     b.city?.toLowerCase().includes(locationFilter.toLowerCase()) ||
                     b.state?.toLowerCase().includes(locationFilter.toLowerCase());
    return matchName && matchLoc;
  });

  const uniqueStates = [...new Set(breeders.map(b => b.state).filter(Boolean))];

  return (
    <div className="min-h-screen bg-background text-slate-900 font-body antialiased">
      <Navbar variant="default" />

      <main className="pt-20">

        {/* Hero */}
        <section className="relative h-[50vh] min-h-[400px] flex items-end overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/auction-hero(1).jpg"
              alt="Breeders"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.1) 100%)' }} />
          </div>
          <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-6 lg:px-12 pb-16">
            <span className="text-emerald-400 font-bold tracking-[0.4em] uppercase text-xs block mb-3">Verified Network</span>
            <h1 className="text-6xl lg:text-8xl font-headline font-extrabold text-white tracking-tight leading-none">
              Elite <span style={{ color: '#06A788' }}>Breeders</span>
            </h1>
            <p className="text-white/70 text-lg mt-4 max-w-xl font-light">
              Discover India's most trusted livestock farmers and breeders — verified, rated, and ready to supply.
            </p>
          </div>
        </section>

        {/* Search & Filter Bar */}
        <section className="bg-white border-b border-slate-100 sticky top-20 z-40 shadow-sm">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row gap-4 items-center">
            {/* Name search */}
            <div className="flex items-center bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-full flex-1 max-w-md gap-3">
              <span className="material-symbols-outlined text-slate-400 text-sm">search</span>
              <input
                type="text"
                placeholder="Search by name or farm..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent border-none text-sm text-slate-800 placeholder:text-slate-400 focus:ring-0 outline-none w-full"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="text-slate-400 hover:text-primary transition-colors">
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              )}
            </div>

            {/* Location filter */}
            <div className="flex items-center bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-full gap-3">
              <span className="material-symbols-outlined text-slate-400 text-sm">location_on</span>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="bg-transparent border-none text-sm text-slate-800 focus:ring-0 outline-none cursor-pointer pr-2"
              >
                <option value="">All States</option>
                {uniqueStates.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <p className="text-sm text-slate-500 font-medium ml-auto">
              {loading ? 'Loading...' : `${filtered.length} breeders found`}
            </p>
          </div>
        </section>

        {/* Breeders Grid */}
        <section className="py-20 max-w-screen-2xl mx-auto px-6 lg:px-12">

          {error && (
            <div className="text-center py-20 text-red-500 font-semibold">{error}</div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse border border-slate-100">
                  <div className="h-48 bg-slate-100" />
                  <div className="p-8 space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-slate-200" />
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-slate-200 rounded w-3/4" />
                        <div className="h-3 bg-slate-100 rounded w-1/2" />
                      </div>
                    </div>
                    <div className="h-3 bg-slate-100 rounded w-full" />
                    <div className="h-10 bg-slate-100 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-32">
              <span className="material-symbols-outlined text-6xl text-slate-300 block mb-4">person_search</span>
              <p className="text-2xl font-bold text-slate-400">No breeders found</p>
              <p className="text-slate-400 mt-2">Try adjusting your search or location filter</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((breeder) => (
                <div
                  key={breeder.id}
                  onClick={() => navigate(`/breeder/${breeder.public_id || breeder.id}`)}
                  className="group bg-white rounded-3xl overflow-hidden border border-slate-100 cursor-pointer transition-all duration-500 hover:-translate-y-2"
                  style={{ boxShadow: '0 10px 40px -10px rgba(0,107,86,0.08)' }}
                >
                  {/* Cover image */}
                  <div className="relative h-44 bg-gradient-to-br from-emerald-900 to-slate-800 overflow-hidden">
                    <img
                      src="/assets/auction-hero(1).jpg"
                      alt="Farm"
                      className="w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    {/* Verified badge */}
                    <div className="absolute top-4 right-4 bg-emerald-400 text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                      <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: "'FILL' 1" }}>verified</span>
                      Verified
                    </div>
                    {/* Live inventory badge */}
                    {breeder.live_inventory > 0 && (
                      <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-primary px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">
                        {breeder.live_inventory} Live Stock
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  <div className="p-8 space-y-6">
                    {/* Profile row */}
                    <div className="flex items-center gap-4">
                      <div className="relative flex-shrink-0">
                        <div className="w-16 h-16 rounded-full border-4 border-white shadow-lg overflow-hidden bg-slate-100"
                             style={{ marginTop: '-2.5rem' }}>
                          <img
                            src={breeder.profile_image
                              ? `${BASE_URL}${breeder.profile_image}`
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(breeder.name)}&size=100&background=06A788&color=fff`
                            }
                            alt={breeder.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-headline font-bold text-slate-900 truncate">
                          {breeder.businessName || breeder.name}
                        </h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                          <span className="material-symbols-outlined text-primary text-sm">location_on</span>
                          {breeder.city}{breeder.state ? `, ${breeder.state}` : ''}
                        </p>
                      </div>
                    </div>

                    {/* Stats row */}
                    <div className="grid grid-cols-3 gap-4 py-4 border-y border-slate-100">
                      <div className="text-center">
                        <p className="text-2xl font-headline font-bold text-slate-900">
                          {new Date().getFullYear() - new Date(breeder.member_since).getFullYear()}
                          <span className="text-primary text-base">yr</span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Legacy</p>
                      </div>
                      <div className="text-center border-x border-slate-100">
                        <p className="text-2xl font-headline font-bold text-slate-900">
                          {breeder.live_inventory}
                          <span className="text-primary text-base">+</span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Inventory</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-headline font-bold text-slate-900">
                          {breeder.total_sales}
                          <span className="text-primary text-base">+</span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Sales</p>
                      </div>
                    </div>

                    {/* CTA buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/breeder/${breeder.public_id || breeder.id}`); }}
                        className="flex-1 py-3 rounded-full bg-slate-900 text-white font-bold text-sm tracking-wide hover:bg-primary transition-all"
                      >
                        View Profile
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/search?seller_id=${breeder.id}&seller_name=${encodeURIComponent(breeder.name)}`);
                        }}
                        className="flex-1 py-3 rounded-full border-2 border-primary text-primary font-bold text-sm tracking-wide hover:bg-primary hover:text-white transition-all"
                      >
                        View Stock
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer variant="default" />
    </div>
  );
};

export default BreedersList;
